/**
 * Admin Results tab for Trailblazers games.
 *
 * Renders a team leaderboard sorted by cumulative team.totalScore, plus each
 * team's treasure haul (rewards won by being first to a planet, after any
 * Treasure Thief sabotage) so an admin can tally payouts after the game.
 *
 * The haul is drawn by the SHARED `TrlTreasureRows`, the same component the
 * player's overlay and haul panel use, fed by `groupTrlTreasureItems`. That
 * grouping is load-bearing, not decoration: an award list is one entry per
 * PLACEMENT, so a team that won eighteen copies of one Cartel pack used to
 * print "Cartel pack" eighteen times across the panel. Grouped, it is one row
 * reading "×18" — which is also the number an admin actually pays out.
 *
 * Data lives on the Game object (selectedGame.trlTeams / trlTreasureAwards) so
 * no refresh endpoint is needed.
 */

import { useMemo } from 'react';
import { MEDALS, computeTrlTeamHauls, groupTrlTreasureItems } from '@swtorbit/shared-socket-events';
import { TrlTreasureRows, leaderboardRowClass } from '@swtorbit/shared-components';
import { useGameContext } from '../../contexts/GameContext';
import { useTrlContext } from '../../contexts/TrlContext';
import { resolveServerUrl } from '../../utils/serverUrl';
import { ResultsTabBase } from '../results';
import { TrlStatsPanel } from './stats/TrlStatsPanel';

export function TrlResultsTab() {
  const { selectedGame } = useGameContext();
  const { trlAdminResults, refreshTrlAdminResults } = useTrlContext();

  const isCompleted = selectedGame?.status === 'completed';

  /**
   * Standings order.
   *
   * Prefers the server snapshot's `placement`, which breaks a score tie on who
   * finished first — B8JS ended with two teams on 4900 that the plain score sort
   * below ordered arbitrarily. Falls back to score alone until the snapshot
   * lands, so the tab renders immediately rather than waiting on a round-trip.
   */
  const leaderboard = useMemo(() => {
    const teams = selectedGame?.trlTeams ?? [];
    const placementByTeam = new Map(
      (trlAdminResults?.teams ?? []).map((t) => [t.teamId, t.placement]),
    );
    return [...teams].sort((a, b) => {
      const pa = placementByTeam.get(a.id);
      const pb = placementByTeam.get(b.id);
      if (pa != null && pb != null) return pa - pb;
      return b.totalScore - a.totalScore;
    });
  }, [selectedGame?.trlTeams, trlAdminResults?.teams]);

  /**
   * teamId → its haul as display rows: everything the team won, with Treasure
   * Thief sabotage applied (stolen items move to the thief), then collapsed so
   * credits and augments each sum to one row and repeated cartel/custom prizes
   * group into a single row carrying the copy count.
   */
  const haulRowsByTeam = useMemo(() => {
    const byTeam = new Map<string, ReturnType<typeof groupTrlTreasureItems>>();
    const hauls = computeTrlTeamHauls(selectedGame?.trlTreasureAwards, selectedGame?.trlSabotageEffects);
    for (const [teamId, haul] of hauls) {
      const rows = groupTrlTreasureItems(haul.map((held) => held.item)).map((row) =>
        // Cartel icons are stored root-relative; resolve them against the API.
        row.icon.kind === 'cartel' && row.icon.url
          ? { ...row, icon: { ...row.icon, url: resolveServerUrl(row.icon.url) } }
          : row,
      );
      byTeam.set(teamId, rows);
    }
    return byTeam;
  }, [selectedGame?.trlTreasureAwards, selectedGame?.trlSabotageEffects]);

  return (
    <ResultsTabBase
      game={selectedGame}
      hasSelectedGame={!!selectedGame}
      title="🧭 Results"
      onRefresh={refreshTrlAdminResults}
      lastRefreshedUtcMs={trlAdminResults?.generatedAtUtcMs}
    >
      {leaderboard.length === 0 ? (
        <div className="bg-space bg-o-50 text-cyan p-sm rounded-base border-md border-blue-light">
          No teams yet — create teams to see the leaderboard.
        </div>
      ) : (
        <div
          className={`p-lg border-md rounded-base${
            isCompleted ? ' border-gold glow glow-gold' : ' border-blue-light glow glow-blue-light'
          }`}
        >
          <div className="d-flex align-items-center justify-content-center mb-sm gap-sm">
            {isCompleted && <span className="fs-lg">🏆</span>}
            <h3 className={isCompleted ? 'text-gold' : 'text-white'}>
              {isCompleted ? 'Game Results' : 'Current Standings'}
            </h3>
          </div>
          <div className="d-flex flex-column gap-sm">
            {leaderboard.map((team, idx) => {
              const rows = haulRowsByTeam.get(team.id) ?? [];

              return (
                /*
                 * Each team is one opaque card. The haul used to be bare text on
                 * the tab's galaxy backdrop, which is why it was unreadable —
                 * giving the block its own surface is the fix, not a colour
                 * change on the text.
                 */
                <div
                  key={team.id}
                  className="bg-space bg-o-50 rounded-base border-md border-blue-light p-xs d-flex flex-column gap-xs"
                >
                  <div className={leaderboardRowClass(idx)}>
                    <span className="leaderboard__rank">{idx < 3 ? MEDALS[idx] : `${idx + 1}.`}</span>
                    <span className="leaderboard__name">{team.name}</span>
                    <span className="leaderboard__stats">
                      {team.members.length} {team.members.length === 1 ? 'player' : 'players'}
                    </span>
                    <span className="leaderboard__score">{team.totalScore} pts</span>
                  </div>

                  {rows.length > 0 ? (
                    <div className="px-xs pb-2xs">
                      <TrlTreasureRows rows={rows} />
                    </div>
                  ) : (
                    <span className="fs-sm text-cyan px-xs pb-2xs">No treasure claimed.</span>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/*
        The statistics dashboard. Rendered only once teams exist, since every
        card is keyed off them and an empty game would be a stack of empty
        cards saying nothing.
      */}
      {leaderboard.length > 0 && <TrlStatsPanel />}
    </ResultsTabBase>
  );
}
